// DUPLICATED from packages/frontend for the studio (intentional — see
// docs/superpowers/specs/2026-06-01-battleplan-authoring-tool-design.md §5).
// Keep rendering in sync with the frontend original when it changes.
import pluralize, { isPlural } from 'pluralize';
import { hasKeyword, namesEqual, regimentOptionsToString } from '@scrolldex/shared';
import {
  ArmyOptionType,
  armyOptionTypeToString,
  type IApdRegimentOptions,
  type IArmyOption,
  type IModel,
  type IUnit,
} from '@scrolldex/shared';

export function formatText(text: string | null, dontBreakBullets?: boolean): string {
  if (!text) return '';

  // markdown-style bullets: a line starting with "* " or "- " becomes a • bullet
  // (authors can write either; normalize so they render the same as • bullets)
  text = text.replace(/(^|\n)[ \t]*[*-][ \t]+/g, '$1• ');
  // bullet point
  if (!dontBreakBullets) text = text.replace(/•\s*/g, '<br>• ');
  // **text** => bold
  text = text.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');
  // *text* => italics
  text = text.replace(/\*(?!\*)(.+?)\*(?!\*)/g, '<i>$1</i>');
  // ^^text^^ => small caps
  text = text.replace(/\^\^(.+?)\^\^/g, '<span style="font-variant: small-caps;">$1</span>');

  // Battleplan objective markers: colored circles after bolded names
  const objectiveMarkers: Record<string, string> = {
    // GHB 2025-26 (Scourge of Ghyran)
    Oakenbrow: 'var(--color-red)',
    Gnarlroot: 'var(--color-green)',
    Heartwood: 'var(--color-blue)',
    Winterleaf: 'var(--color-purple)',
    // GHB 2026-27 (Scourge of Aqshy)
    'Sun Seekers': 'var(--color-yellow)',
    'Golden Lions': 'var(--color-red)',
    Heldenhain: 'var(--color-black)',
    Sigmarite: 'var(--color-blue)',
  };
  for (const [name, color] of Object.entries(objectiveMarkers)) {
    const circle = `<span style="display:inline-block;width:0.7em;height:0.7em;border-radius:50%;background:${color};margin-left:0.25em;vertical-align:middle;"></span>`;
    text = text.replaceAll(`<b>${name}</b>`, `<b>${name}</b>${circle}`);
  }

  text = text.replace(/\n/g, '<br>');

  // replace repeated <br>s with a single <br>
  text = text.replace(/(<br>\s*)+/g, '<br>');

  text = text.replace(/(<br>\s*)+/g, '<br>');

  return text as string;
}

export function formatModelGroups(unit: IUnit): string {
  // contains champion keyword case insensitive
  const hasChampion = hasKeyword('champion', unit.keywords);
  const isOneGroup = unit.models.length == 1;

  let text = '';
  if (!isOneGroup) {
    text += '<ul>';
  }

  const unitSize: number = Number(unit.battleProfile?.unit_size ?? 1);

  for (const group of unit.models) {
    const groupText = formatModelGroup(unitSize, group, hasChampion);
    if (!isOneGroup) {
      text += `<li>${groupText}</li>`;
    } else {
      text += groupText;
    }
  }

  if (!isOneGroup) {
    text += '</ul>';
  }

  return text;
}

function formatModelGroup(unitSize: number, group: IModel, hasChampion: boolean): string {
  const isOneModel = group.max === 1;
  const isGroupSize = unitSize === group.max;
  let text = '<i>';

  if (isOneModel) {
    if (!isGroupSize) {
      text += `1 model is a <b>${group.name}</b>`;
    } else {
      text += `<b>${group.name}</b>`;
    }
  } else if (isGroupSize) {
    text += `each <b>${group.name}</b>`;
  } else {
    text += `${group.max} in ${unitSize} models are <b>${pluralize(group.name)}</b>`;
  }

  // w.max !== 0 is to filter out the unmade champions weapon
  // this will potentially also filter anvil weapons depending on
  // how we handle them
  const defaultWeapons = Object.values(group.weapons.basic)
    .filter((w) => w.max !== 0)
    .flatMap((s) => s.weapons);
  if (defaultWeapons.length > 0) {
    if (!isOneModel && !isGroupSize) {
      text += ' each';
    }
    if ((isOneModel && isGroupSize) || !isOneModel) {
      text += ' is';
    }

    text += ' armed with';
    // weapon1,weapon2...and weaponN`;
    if (defaultWeapons.length == 1) {
      text += ` ${formatWeaponName(defaultWeapons[0].name)}`;
    } else if (defaultWeapons.length > 1) {
      text += ` ${defaultWeapons
        .map((w) => formatWeaponName(w.name))
        .join(', ')
        .replace(/, ([^,]*)$/, ' and $1')}`;
    }
  }

  // this is a hack for unmade
  if (namesEqual(group.name, 'the unmade')) {
    const champWeapon = Object.values(group.weapons.basic)
      .filter((w) => w.max === 0)
      .flatMap((s) => s.weapons);
    text += `.<br/><ul><li>The champion must replace their `;
    text += `<b>${defaultWeapons[0].name}</b> with <b>${champWeapon[0].name}</b>.</li></ul>`;
  }

  text += '</i>';

  // now in a bullet list write out optional weapons
  const optionalWeapons = Object.values(group.weapons.advanced);
  if (optionalWeapons.length > 0) {
    text += '<ul>';
    for (const weapon of optionalWeapons) {
      const isOptionGroupSize = weapon.max === group.max;
      text += '<li>';
      if (isOneModel) {
        text += `<i>it may be armed with ${formatWeaponName(weapon.name)}`;
      } else if (isOptionGroupSize) {
        text += `<i>each may be armed with ${formatWeaponName(weapon.name)}`;
      } else {
        text += `<i>${weapon.max} in ${group.max} may be armed with ${formatWeaponName(weapon.name)}`;
      }

      if (weapon.replaces && weapon.replaces.length > 0) {
        const replacesAll = ((): boolean => {
          const numBasic = Object.values(group.weapons.basic).length;
          return numBasic === weapon.replaces.length;
        })();

        if (replacesAll) {
          text += ' instead';
        } else {
          text += ` instead of ${weapon.replaces.map((w) => formatWeaponName(w)).join(' and ')}`;
        }
      }

      text += '</i></li>';
    }

    if (hasChampion) {
      text += '<li><i>The champion cannot replace their weapon</i></li>';
    }

    text += '</ul>';
  }

  // grouped weapons
  const weaponSets = group.optionSets.filter((set) =>
    set.options.some((option) => option.weapons.length > 0),
  );

  weaponSets.forEach((set) => {
    text += '<br>';
    text += '<i>It may be armed with 1 of the following options:</i>';
    text += '<ul>';
    for (const option of set.options) {
      const weaponNames = option.weapons.map((w) => w.name).join(', ');
      text += `<li><i> ${formatWeaponName(weaponNames)} </i></li>`;
    }
    text += '</ul>';
  });

  if (weaponSets.length === 0 && defaultWeapons.length === 0 && optionalWeapons.length === 0) {
    return '';
  }

  return text;
}

function formatWeaponName(name: string): string {
  if (isPlural(name)) {
    return `<b>${name}</b>`;
  }
  // decide a or an
  const firstLetter = name.charAt(0).toLowerCase();
  const article = ['a', 'e', 'i', 'o', 'u'].includes(firstLetter) ? 'an' : 'a';
  return `${article} <b>${name}</b>`;
}

export function formatSubHeroTags(tags: string[]): string {
  if (!tags || tags.length === 0) return '';
  return `<i>This <b>Hero</b> can join an eligible regiment as a ${tags.map((tag) => `<b>${tag}</b>`).join(', ')}.</i>`;
}

export function formatRegimentOptions(options: IApdRegimentOptions[]): string {
  const formatItems = (opts: IApdRegimentOptions[]) =>
    opts
      .map((opt) => {
        return `<li>${regimentOptionsToString(opt, true)}</li>`;
      })
      .join('');

  let html = '';
  if (options.length > 0) {
    html += "<div style='font-weight:600;margin-bottom:0.2em;'>Regiment Options:</div>";
    html += `<ul style='margin:0 0 0 1.2em;padding:0;font-size:0.97em;'>${formatItems(options)}</ul>`;
  }

  return html;
}

export function formatArmyOptions(options: IArmyOption[]): string {
  let out = '';
  for (const option of options) {
    out += `<li>`;
    console.log('Formatting army option:', option, armyOptionTypeToString(option.type));
    switch (option.type) {
      case ArmyOptionType.RequiredGeneral:
        out += formatRequiredGeneral(option.units);
        break;
      case ArmyOptionType.GeneralIfIncluded:
        out += formatMustBeGeneralIfIncluded(option.units);
        break;
      case ArmyOptionType.MustBeIncluded:
        out += formatMustBeIncluded(option);
        break;
      case ArmyOptionType.GeneralIfHero:
        out += formatGeneralIfHero(option);
        break;
      case ArmyOptionType.OnePerX:
        out += formatOnePerX(option);
        break;
      case ArmyOptionType.Custom:
        if (option.note) {
          out += option.note;
        }
        break;
      default:
        out += `Unknown army option type: ${option.type}`;
    }
    out += `</li>`;
  }
  return out;
}

function formatRequiredGeneral(requiredGeneral: string[]): string {
  if (requiredGeneral.length === 1) {
    return `<strong>${requiredGeneral[0]}</strong> must be the general.`;
  } else if (requiredGeneral.length > 1) {
    return `One of the following units must be included and be the general: <strong>${requiredGeneral.join(', ')}</strong>`;
  }
  return '';
}

function formatMustBeGeneralIfIncluded(units: string[]): string {
  if (!units || units.length === 0) return '';
  if (units.length === 1) {
    return `<strong>${units[0]}</strong> must be the general if included.`;
  } else {
    return `One of the following must be the general if included: <strong>${units.join(', ')}</strong>`;
  }
}

function formatMustBeIncluded(option: IArmyOption): string {
  if (option.min === 0 && option.max === 0) {
    return `No units must be included.`;
  } else if (option.min === option.max) {
    return `exactly ${option.min} <strong>${option.units.join(', ')}</strong> must be included.`;
  } else {
    return `${option.min}-${option.max} <strong>${option.units.join(', ')}</strong> can be included.`;
  }
}

function formatGeneralIfHero(option: IArmyOption): string {
  if (!option.units || option.units.length === 0) return '';
  return `${formatWeaponName(option.units.join(', '))} may be a hero, if it is it must be the general.`;
}

function formatOnePerX(option: IArmyOption): string {
  if (!option.enablingUnit || !option.onePerUnit) return '';
  return `For every <strong>${regimentOptionsToString(option.enablingUnit, false, true)}</strong> unit you may include a <strong>${regimentOptionsToString(option.onePerUnit, false, true)}</strong>.`;
}

export function formatTextWithSearch(text: string, searchQuery?: string): string {
  if (!text) return '';

  // First, format the text as usual
  const formatted = formatText(text, true);

  if (!searchQuery || !searchQuery.trim()) return formatted;

  // Split into text and tags
  const parts = formatted.split(/(<[^>]+>)/g);
  const queryRegex = new RegExp(searchQuery, 'gi');

  for (let i = 0; i < parts.length; i++) {
    // Only highlight in text nodes (not tags)
    if (!parts[i].startsWith('<')) {
      parts[i] = parts[i].replace(queryRegex, (match) => `<span class="highlight">${match}</span>`);
    }
  }

  return parts.join('');
}
